import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

// components
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { Checkbox } from 'primereact/checkbox'

import ChartButton from '@aytm/app/stats/page/question-views/vk/components/views/chart/components/chart-buttons/ChartButton'
import ChartButtonsPanel from '@aytm/app/stats/page/question-views/vk/components/views/chart/components/chart-buttons/ChartButtonsPanel'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

import { isComponentOn } from '@aytm/app/stats/page/question-views/components/lib/utils'
import { useChartButtonsForm } from '@aytm/app/stats/page/question-views/vk/components/views/chart/components/chart-buttons/lib/hooks'

import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

// styles
import style from '@aytm/app/stats/page/question-views/vk/components/views/chart/components/chart-buttons/styles/ChartButtons.module.scss'
import { NO_PRINT } from '@aytm/app/stats/page/question-views/components/lib/constants'

const ChartButtons = ({ id, components, config, period, product, answer, type, onModelChange }) => {
  const bestEstimate = (answer?.pointsOfInterest?.[`p${period}`] || []).find(p => p['_Name'] === 'clients_price')
  const bestEstimatePrice = bestEstimate?.['UNIT.PRICE'] || ''

  const {
    form,
    active,
    handleClick,
    handleAddCostItem,
    handleInputChange,
    handleCheckboxChange,
    setCheckbox,
    handleInputKeyPress,
    handleNestedInputChange,
    handleRemoveCostItem
  } = useChartButtonsForm({ answer, config, onModelChange })

  const handleInfographicClick = () => {
    onModelChange({
      infographic: true
    })
  }

  return (
    <div className={`${style.wrapper} ${NO_PRINT}`}>
      <div className={style.buttons}>
        {isComponentOn(components, 'edit_controls') && (
          <>
            {[2, 3, 4, 5, 6].includes(product) && bestEstimate && (
              <ChartButton buttonName="price" active={active} touched={form.price.isTouched} handleClick={handleClick}>
                {active === 'price' ? 'Update' : 'Edit'} Estimated
              </ChartButton>
            )}
            {[5, 6].includes(product) && (
              <ChartButton buttonName="cost" active={active} touched={form.cost.isTouched} handleClick={handleClick}>
                {active === 'cost' ? 'Update' : 'Edit'} Cost
              </ChartButton>
            )}
            {[2, 4, 5, 6].includes(product) && (
              <ChartButton buttonName="tam" active={active} touched={form.tam.isTouched} handleClick={handleClick}>
                {active === 'tam' ? 'Update' : 'Edit'} TAM
              </ChartButton>
            )}
          </>
        )}
        {isComponentOn(components, 'infographic') && (
          <Button
            className={'p-button-rounded p-button-blue'}
            icon={`icon-aytmfd-${PageGlobals.QUESTION_TYPE_ICONS[type]}`}
            label="Open Infographic"
            onClick={handleInfographicClick}
          />
        )}
      </div>
      <div className={style.content}>
        {active === 'price' && (
          <ChartButtonsPanel
            title="ESTIMATED RETAILS PRICE"
            tooltip="This is the estimated retail price provided before the fielding. You can see in the table below - the revenue, sales volumes and profit projections for this amount. You can adjust this amount and see what to expect at any other price point."
            text={`Your best estimate of the retail PRICE was ${bestEstimatePrice}. Here you can <br/>play with a different number to run "what if" experiments.`}
          >
            <InputText
              aria-label="Estimated retail price"
              value={form.price.value}
              name="price"
              onChange={handleInputChange}
              onKeyPress={handleInputKeyPress}
            />
          </ChartButtonsPanel>
        )}
        {active === 'cost' && (
          <ChartButtonsPanel
            title="Your Unit Cost"
            tooltip="This is the estimated unit cost of your product or service. The unit price may include R&D costs, manufacturing, marketing, shipping, packaging etc. allowing to get to cleaner profit calculations. Feel free to adjust it here and see how it changes Van Konan projections of optimal price points and market opportunities.<br/>You can also reflect the economy of scale if your product or service becomes less expensive to obtain for you as quantity increases. You can add up to 11 lines with amount and volume in the price grid to outline your cost structure."
            text="What's the best estimate of your COST?<br/>Add more lines here if your cost is volume sensitive"
            footer={
              <div className={style.checkbox}>
                <Checkbox
                  aria-labelledby={`${id}-incr-label`}
                  inputId={`${id}-incr`}
                  name="incr"
                  checked={form.incr.value}
                  onChange={handleCheckboxChange}
                />
                <span
                  id={`${id}-incr-label`}
                  className="p-checkbox-label"
                  onClick={() => setCheckbox('incr', !form.incr.value)}
                >
                  Cost changes with 1 unit increment. We&apos;ll take your grid and calculate smooth price change
                  gradient
                </span>
              </div>
            }
          >
            <div className={style.editCostGroup}>
              {form.cost.value.map(({ id, value, quantity }, index) => (
                <div key={id} data-testid="stat-vk-cost-row" className={style.editCostRow}>
                  {index !== 0 && (
                    <span
                      data-testid="stat-vk-cost-delete"
                      className={style.editCostDelete}
                      aria-label="Remove line"
                      {...pressable(() => handleRemoveCostItem(id))}
                    >
                      <i className="icon-aytmfd-x"></i>
                    </span>
                  )}
                  <InputText
                    className={style.editCostInput}
                    value={value}
                    name="value"
                    placeholder="Unit Cost"
                    onChange={event => handleNestedInputChange('cost', event, id)}
                  />
                  <InputText
                    className={style.editCostInput}
                    value={quantity}
                    name="quantity"
                    placeholder="Quantity"
                    onChange={event => handleNestedInputChange('cost', event, id)}
                  />
                </div>
              ))}
              <span
                data-testid="stat-vk-cost-add"
                className={style.editCostAddButton}
                {...pressable(handleAddCostItem)}
              >
                <i className="icon-aytmfd-add" />
                Update line to the grid
              </span>
            </div>
          </ChartButtonsPanel>
        )}
        {active === 'tam' && (
          <ChartButtonsPanel
            title="Market<br />Size"
            tooltip="This is the estimated total addressable market or TAM, provided before the fielding. By default, we use 1000 consumers. Revenue, buyers, profit, shown below were calculated, based on this number and adjusted by probability, frequency, and any applied filters on this page. You can adjust this amount here and see how it changes your market opportunity. For example, you may know that 40% of country's population fall into a broad category of your potential users. You can easily calculate that number or reduce it further by anticipating competition and your realistic market share or leave wider if you want to measure the ceiling of the market opportunity."
            text="What's your best estimate of the Total Addressable Market(TAM)?<br/>Default = 1000"
          >
            <InputText
              aria-label="Total addressable market"
              value={form.tam.value}
              name="tam"
              onChange={handleInputChange}
              onKeyPress={handleInputKeyPress}
            />
          </ChartButtonsPanel>
        )}
      </div>
    </div>
  )
}

ChartButtons.propTypes = {
  id: PropTypes.string,
  components: PropTypes.object,
  period: PropTypes.number,
  product: PropTypes.number,
  answer: answerPropType,
  config: PropTypes.object,
  type: PropTypes.string,
  onModelChange: PropTypes.func
}

export default ChartButtons
